import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import './Discover.css';
import logoSrc from '../icons/logo_letter.svg';
import placeholderSrc from '../images/placeholder.png';
import markerClinic from '../icons/marker_clinic.png';
import markerVet from '../icons/marker_vet.png';
import markerOther from '../icons/marker_other.png';
import checkIconSrc from '../icons/check.svg';
import { tokenService } from '../services/tokenService.js';
import { ReactComponent as Logo } from '../icons/logo_letter.svg';
import { formatPhone } from '../utils/formatters.js';

// ─── Constants ──────────────────────────────────────────────────────────────

const BASE_URL =
    process.env.REACT_APP_BASE_URL ||
    'https://dolphin-app-5kp7e.ondigitalocean.app';
const API_KEY = process.env.REACT_APP_API_KEY;
const DEFAULT_DISTANCE = 5.0;
const FALLBACK_LAT = -14.235004; // Brazil center
const FALLBACK_LNG = -51.925280;

// ─── Public Axios instance (no auth redirect middleware) ─────────────────────

const publicApi = axios.create({
    baseURL: BASE_URL,
    timeout: 30000,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Resolve the best available token for Discover requests:
 * 1. User token (already logged in) — has most permissions
 * 2. Cached app token (from a previous session or login flow)
 * 3. Fresh app token obtained from v1/api/app/login
 */
async function resolveToken() {
    const userToken = tokenService.getUserToken();
    if (userToken) return userToken;

    const appToken = tokenService.getAppToken();
    if (appToken) return appToken;

    const res = await publicApi.post('/v1/api/app/login', { api_key: API_KEY });
    if (res.data?.error) throw new Error('Erro ao autenticar aplicação');
    const freshToken = res.data?.response;
    tokenService.setAppToken(freshToken);
    return freshToken;
}

function formatDistance(km) {
    if (km == null) return '';
    if (km < 1) return '< 1 km';
    if (km < 10) return `${km.toFixed(1).replace('.', ',')} km`;
    return `${Math.round(km)} km`;
}

function formatRating(score) {
    if (score == null || score < 0) return null;
    return score.toFixed(1);
}

function isPremiumEntity(entity) {
    return (
        entity.plan != null &&
        entity.plan.active === true &&
        typeof entity.plan.planId === 'string' &&
        entity.plan.planId.includes('premium')
    );
}

function isPlusEntity(entity) {
    return (
        entity.plan != null &&
        entity.plan.active === true &&
        typeof entity.plan.planId === 'string' &&
        entity.plan.planId.includes('plus')
    );
}

function sortEntities(list) {
    return [...list].sort((a, b) => {
        const wa = isPremiumEntity(a) ? (a.plan?.weight ?? 4) : 4;
        const wb = isPremiumEntity(b) ? (b.plan?.weight ?? 4) : 4;
        return wa - wb;
    });
}

function loadGoogleMaps() {
    return new Promise((resolve, reject) => {
        if (window.google?.maps) return resolve(window.google.maps);
        // Script already injected but not yet ready
        if (document.querySelector('script[src*="maps.googleapis.com"]')) {
            const interval = setInterval(() => {
                if (window.google?.maps) { clearInterval(interval); resolve(window.google.maps); }
            }, 100);
            return;
        }
        const key = process.env.REACT_APP_GOOGLE_MAPS_KEY || '';
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places&language=pt-BR&region=BR&loading=async`;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve(window.google.maps);
        script.onerror = () => reject(new Error('Falha ao carregar o Google Maps'));
        document.head.appendChild(script);
    });
}

// ─── Location persistence ─────────────────────────────────────────────────────

const LOC_KEY = 'vetsse_loc';

function getSavedLocation() {
    try {
        const raw = localStorage.getItem(LOC_KEY);
        if (!raw) return null;
        const { lat, lng, name } = JSON.parse(raw);
        if (typeof lat !== 'number' || typeof lng !== 'number') return null;
        return { lat, lng, name: name || '' };
    } catch {
        return null;
    }
}

function persistLocation(lat, lng, name) {
    try {
        localStorage.setItem(LOC_KEY, JSON.stringify({ lat, lng, name: name || '' }));
    } catch {}
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function PinIcon() {
    return (
        <svg className="pin-icon" viewBox="0 0 24 24" fill="currentColor" width="12" height="12" aria-hidden="true">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
        </svg>
    );
}

function VerifiedIcon() {
    return (
        <img src={checkIconSrc} className="verified-icon" width="16" height="16" alt="Verificado" aria-label="Verificado" />
    );
}

function StarIcon() {
    return (
        <svg className="star-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
    );
}

function ServiceTags({ services }) {
    const sorted = [...(services || [])].sort((a, b) =>
        (a.service?.slug || '').localeCompare(b.service?.slug || '')
    );
    const visible = sorted.slice(0, 4);
    const extra = sorted.length - 4;
    return (
        <div className="entity-tags">
            {visible.map((es) => (
                <span key={es.id} className="entity-tag">
                    {es.service?.name || '–'}
                </span>
            ))}
            {extra > 0 && <span className="entity-tag entity-tag-more">+{extra}</span>}
        </div>
    );
}

function EntityCard({ entity, onClick }) {
    const premium = isPremiumEntity(entity);
    const firstImage = (entity.images || [])
        .filter((img) => img.active && img.url)
        .sort((a, b) => a.order - b.order)[0];
    const neighborhood = entity.addressNeighborhood || entity.addressCity || '';
    const dist = entity.defaultDistance != null ? formatDistance(entity.defaultDistance) : null;
    const locationLine = [neighborhood, dist].filter(Boolean).join(' \u2022 ');
    const rating = formatRating(entity.ratingScore);

    return (
        <article
            className={`entity-card${premium ? ' premium' : ''}`}
            onClick={() => onClick(entity)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && onClick(entity)}
            aria-label={`Ver detalhes de ${entity.name}`}
        >
            {premium && <div className="card-star-badge" aria-label="Destaque">★</div>}
            <div className="entity-card-inner">
                <div className="entity-avatar" aria-hidden="true">
                    <img
                        src={firstImage ? firstImage.url : placeholderSrc}
                        alt={entity.name}
                        loading="lazy"
                    />
                </div>
                <div className="entity-card-info">
                    <h3 className="entity-name">
                        {entity.name}
                        {premium && <VerifiedIcon />}
                    </h3>
                    {locationLine && (
                        <p className="entity-location">
                            <PinIcon />
                            {locationLine}
                        </p>
                    )}
                    {rating && (
                        <div className="entity-rating-row">
                            <StarIcon />
                            <span className="rating-score">{rating}</span>
                            <span className="rating-count">({entity.ratingCount})</span>
                        </div>
                    )}
                </div>
            </div>
            {entity.services?.length > 0 && (
                <div className="entity-card-tags-row">
                    <ServiceTags services={entity.services} />
                </div>
            )}
        </article>
    );
}

function SkeletonCard() {
    return (
        <div className="entity-card skeleton" aria-hidden="true">
            <div className="entity-card-inner">
                <div className="skeleton-avatar shimmer" />
                <div className="skeleton-info">
                    <div className="skeleton-line shimmer" style={{ width: '70%' }} />
                    <div className="skeleton-line shimmer" style={{ width: '50%', marginTop: 8 }} />
                </div>
            </div>
            <div className="entity-card-tags-row">
                <div className="skeleton-tags">
                    <div className="skeleton-tag shimmer" />
                    <div className="skeleton-tag shimmer" />
                    <div className="skeleton-tag shimmer" />
                </div>
            </div>
        </div>
    );
}

function EmptyState({ onRefresh }) {
    return (
        <div className="discover-empty" role="status">
            <div className="discover-empty-icon" aria-hidden="true">🐾</div>
            <h3>Nenhum veterinário encontrado</h3>
            <p>Tente aumentar o raio de busca, mudar a localização ou remover os filtros.</p>
            {onRefresh && (
                <button className="discover-btn-primary" onClick={onRefresh}>
                    Buscar novamente
                </button>
            )}
        </div>
    );
}

function MapEntityCard({ entity, onOpen }) {
    const premium = isPremiumEntity(entity);
    const rating = formatRating(entity.ratingScore);
    const dist = entity.defaultDistance != null ? formatDistance(entity.defaultDistance) : null;
    const neighborhood = entity.addressNeighborhood || entity.addressCity || '';
    const locationLine = [neighborhood, dist].filter(Boolean).join(' \u2022 ');
    const firstImage = (entity.images || [])
        .filter((i) => i.active && i.url)
        .sort((a, b) => a.order - b.order)[0];

    return (
        <div
            className={`discover-map-card${premium ? ' premium' : ''}`}
            role="button"
            tabIndex={0}
            aria-label={`Ver detalhes de ${entity.name}`}
            onClick={() => onOpen(entity)}
            onKeyDown={(e) => e.key === 'Enter' && onOpen(entity)}
        >
            {premium && <div className="card-star-badge" aria-label="Destaque">★</div>}
            <div className="entity-card-inner">
                <div className="entity-avatar" aria-hidden="true">
                    <img
                        src={firstImage ? firstImage.url : placeholderSrc}
                        alt={entity.name}
                        loading="lazy"
                    />
                </div>
                <div className="entity-card-info">
                    <h3 className="entity-name">
                        {entity.name}
                        {premium && <VerifiedIcon />}
                    </h3>
                    {locationLine && (
                        <p className="entity-location">
                            <PinIcon />
                            {locationLine}
                        </p>
                    )}
                    {rating && (
                        <div className="entity-rating-row">
                            <StarIcon />
                            <span className="rating-score">{rating}</span>
                            <span className="rating-count">({entity.ratingCount})</span>
                        </div>
                    )}
                </div>
                <svg className="map-card-chevron" viewBox="0 0 24 24" fill="currentColor" width="18" height="18" aria-hidden="true">
                    <path d="M9.29 6.71a.996.996 0 000 1.41L13.17 12l-3.88 3.88a.996.996 0 101.41 1.41l4.59-4.59a.996.996 0 000-1.41L10.7 6.7c-.38-.38-1.02-.38-1.41.01z"/>
                </svg>
            </div>
            {entity.services?.length > 0 && (
                <div className="entity-card-tags-row">
                    <ServiceTags services={entity.services} />
                </div>
            )}
        </div>
    );
}

// ─── Entity Details Helpers ────────────────────────────────────────────────────

function formatReviewDate(dateStr) {
    if (!dateStr) return '';
    try { return new Date(dateStr).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' }); } catch { return ''; }
}

const ENTITY_TYPE_MAP = {
    company: 'Estabelecimento',
    personal: 'Veterinário(a)',
    clinic: 'Clínica Veterinária',
    hotel: 'Hotel Pet',
    pet_shop: 'Pet Shop',
};
function getEntityTypeLabel(type) { return ENTITY_TYPE_MAP[type] || 'Veterinário'; }

const SCHED_DAY_NAMES = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
function parseScheduleDays(str) {
    if (!str || str.length < 7) return null;
    return SCHED_DAY_NAMES.map((n, i) => ({ name: n, on: str[i] === '1' }));
}

// ─── Static map circle helper ───────────────────────────────────────────────────

function buildStaticMapCirclePath(lat, lng, radiusKm, points = 32) {
    const R = 6371;
    const d = radiusKm / R;
    const latR = (lat * Math.PI) / 180;
    const lngR = (lng * Math.PI) / 180;
    const pts = [];
    for (let i = 0; i <= points; i++) {
        const bearing = (2 * Math.PI * i) / points;
        const pLat = Math.asin(
            Math.sin(latR) * Math.cos(d) +
            Math.cos(latR) * Math.sin(d) * Math.cos(bearing)
        );
        const pLng = lngR + Math.atan2(
            Math.sin(bearing) * Math.sin(d) * Math.cos(latR),
            Math.cos(d) - Math.sin(latR) * Math.sin(pLat)
        );
        pts.push(`${((pLat * 180) / Math.PI).toFixed(6)},${((pLng * 180) / Math.PI).toFixed(6)}`);
    }
    // 0x7165ff26 = primary color at 15% alpha (0x26 ≈ 0.15 × 255)
    return `&path=color:0x7165ff99|fillcolor:0x7165ff26|weight:2|${pts.join('|')}`;
}

// ─── Entity Details Modal ─────────────────────────────────────────────────────

function EntityDetailsModal({ entityId, token, defaultLat, defaultLong, onClose, onNameLoad }) {
    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentImg, setCurrentImg] = useState(0);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [descExpanded, setDescExpanded] = useState(false);
    const [servicesExpanded, setServicesExpanded] = useState(false);
    const [insurancesExpanded, setInsurancesExpanded] = useState(false);

    useEffect(() => {
        if (!entityId || !token) return;
        setLoading(true);
        setError(null);
        setCurrentImg(0);
        publicApi
            .get('/v1/api/entities/details', {
                headers: { Authorization: token },
                params: {
                    subId: entityId,
                    ...(defaultLat != null && { defaultLat: String(defaultLat) }),
                    ...(defaultLong != null && { defaultLong: String(defaultLong) }),
                },
            })
            .then((res) => {
                const data = res.data?.response || null;
                if (!data || data.active === false) {
                    toast.error('Veterinário não encontrado ou indisponível.');
                    onClose();
                    return;
                }
                setDetails(data);
                if (data.name && onNameLoad) onNameLoad(data.name);
            })
            .catch(() => setError('Não foi possível carregar os detalhes.'))
            .finally(() => setLoading(false));
    }, [entityId, token]);

    // Lock body scroll while open
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, []);

    const images = details
        ? (details.images || []).filter((i) => i.active && i.url).sort((a, b) => a.order - b.order)
        : [];

    const premium = details ? isPremiumEntity(details) : false;
    const plus = details ? isPlusEntity(details) : false;
    const canSchedule = details?.active && details?.scheduleActive && (premium || plus);
    const rating = details ? formatRating(details.ratingScore) : null;
    const typeLabel = details ? getEntityTypeLabel(details.type) : '';
    const scheduleDaysArr = details ? parseScheduleDays(details.scheduleDays) : null;
    const dist = details?.defaultDistance != null ? formatDistance(details.defaultDistance) : null;

    const addressFull = details
        ? [
            details.address && details.addressNumber
                ? `${details.address}, ${details.addressNumber}`
                : details.address || '',
            details.addressNeighborhood || '',
            details.addressCity
                ? `${details.addressCity}${details.addressUF ? ` - ${details.addressUF}` : ''}`
                : '',
            details.addressZIP || '',
          ].filter(Boolean).join(' - ')
        : '';

    const mapsNavUrl = addressFull
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressFull)}`
        : null;

    const mapZoom = (() => {
        if (!details?.showAddress && details?.serviceRangeActive) {
            const raio = (details.serviceRange > 0 ? details.serviceRange : 1) * 1500;
            return Math.max(1, Math.min(20, Math.round(16 - Math.log((raio * 1.5) / 500) / Math.log(2))));
        }
        return 15;
    })();
    const staticMapUrl = details?.lat && details?.long
        ? `https://maps.googleapis.com/maps/api/staticmap?center=${details.lat},${details.long}&zoom=${mapZoom}&size=600x200&scale=2&language=pt-BR&key=${process.env.REACT_APP_GOOGLE_MAPS_KEY || ''}${
            details.serviceRangeActive && details.serviceRange > 0
                ? buildStaticMapCirclePath(details.lat, details.long, details.serviceRange)
                : ''
          }`
        : null;

    async function handleCopyAddress() {
        if (!addressFull) return;
        try {
            await navigator.clipboard.writeText(addressFull);
            toast.success('Endereço copiado para a área de transferência!');
        } catch {
            toast.error('Erro ao copiar endereço');
        }
    }

    function handleOverlayClick(e) {
        if (e.target === e.currentTarget) onClose();
    }

    return (
        <div
            className="entity-details-overlay"
            role="dialog"
            aria-modal="true"
            aria-label={details?.name || 'Detalhes'}
            onClick={handleOverlayClick}
        >
            <div className="entity-details-modal">

                {/* ── Gallery ── */}
                <div className="entity-details-gallery">
                    {loading ? (
                        <div className="entity-details-gallery-skeleton shimmer" />
                    ) : images.length > 0 ? (
                        <>
                            <img
                                src={images[currentImg]?.url}
                                alt={details?.name}
                                className="entity-details-gallery-img entity-details-gallery-clickable"
                                onClick={() => setLightboxOpen(true)}
                            />
                            {images.length > 1 && (
                                <>
                                    <button
                                        className="gallery-nav gallery-nav-prev"
                                        onClick={() => setCurrentImg((p) => (p - 1 + images.length) % images.length)}
                                        aria-label="Foto anterior"
                                    >
                                        <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>
                                    </button>
                                    <button
                                        className="gallery-nav gallery-nav-next"
                                        onClick={() => setCurrentImg((p) => (p + 1) % images.length)}
                                        aria-label="Próxima foto"
                                    >
                                        <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>
                                    </button>
                                </>
                            )}
                            <div className="gallery-counter" aria-label={`Foto ${currentImg + 1} de ${images.length}`}>
                                {currentImg + 1}/{images.length}
                            </div>
                        </>
                    ) : (
                        <img src={placeholderSrc} alt="Sem foto" className="entity-details-gallery-img entity-details-gallery-placeholder" />
                    )}

                    {/* Bottom gradient overlay: name, type badge, rating */}
                    {!loading && details && (
                        <div className="entity-details-gallery-overlay">
                            <div className="entity-details-gallery-info">
                                <div className="entity-details-gallery-badges-row">
                                    {premium && (
                                        <span className="entity-details-premium-pill">
                                            <svg viewBox="0 0 24 24" fill="currentColor" width="11" height="11" aria-hidden="true" style={{ flexShrink: 0 }}><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg> Em Destaque
                                        </span>
                                    )}
                                </div>
                                <h2 className="entity-details-gallery-name">{details.name}</h2>
                                <div className="entity-details-gallery-meta">
                                    {(rating || details.ratingCount > 0) && (
                                        <>
                                            <StarIcon />
                                            <span className="entity-details-gallery-score">{rating || '–'}</span>
                                            <span className="entity-details-gallery-count">
                                                ({details.ratingCount} {details.ratingCount === 1 ? 'avaliação' : 'avaliações'})
                                            </span>
                                        </>
                                    )}
                                    {dist && <span className="entity-details-gallery-dist">· {dist}</span>}
                                </div>
                            </div>
                        </div>
                    )}

                    <button className="entity-details-close" onClick={onClose} aria-label="Fechar">
                        <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                        </svg>
                    </button>
                </div>

                {/* ── Lightbox ── */}
                {lightboxOpen && images.length > 0 && (
                    <div
                        className="entity-lightbox-overlay"
                        onClick={() => setLightboxOpen(false)}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Visualizar foto"
                    >
                        <button
                            className="entity-lightbox-close"
                            onClick={() => setLightboxOpen(false)}
                            aria-label="Fechar"
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                            </svg>
                        </button>
                        <img
                            src={images[currentImg]?.url}
                            alt={details?.name}
                            className="entity-lightbox-img"
                            onClick={(e) => e.stopPropagation()}
                        />
                        {images.length > 1 && (
                            <>
                                <button
                                    className="entity-lightbox-nav entity-lightbox-prev"
                                    onClick={(e) => { e.stopPropagation(); setCurrentImg((p) => (p - 1 + images.length) % images.length); }}
                                    aria-label="Foto anterior"
                                >
                                    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>
                                </button>
                                <button
                                    className="entity-lightbox-nav entity-lightbox-next"
                                    onClick={(e) => { e.stopPropagation(); setCurrentImg((p) => (p + 1) % images.length); }}
                                    aria-label="Próxima foto"
                                >
                                    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>
                                </button>
                                <div className="entity-lightbox-counter">{currentImg + 1}/{images.length}</div>
                            </>
                        )}
                    </div>
                )}

                {/* ── Scrollable body ── */}
                <div className="entity-details-body">
                    {loading ? (
                        <div className="entity-details-shimmer-body">
                            <div className="shimmer entity-details-shimmer-line" style={{ width: '65%', height: 24 }} />
                            <div className="shimmer entity-details-shimmer-line" style={{ width: '40%', height: 16, marginTop: 10 }} />
                            <div className="shimmer entity-details-shimmer-line" style={{ width: '55%', height: 14, marginTop: 8 }} />
                            <div style={{ marginTop: 24 }}>
                                <div className="shimmer entity-details-shimmer-line" style={{ width: '30%', height: 16 }} />
                                <div className="shimmer entity-details-shimmer-line" style={{ width: '100%', height: 12, marginTop: 10 }} />
                                <div className="shimmer entity-details-shimmer-line" style={{ width: '80%', height: 12, marginTop: 6 }} />
                            </div>
                        </div>
                    ) : error ? (
                        <div className="entity-details-error">
                            <span>⚠️</span>
                            <p>{error}</p>
                        </div>
                    ) : details ? (
                        <>
                            {/* ── Quick info + address + description ── */}
                            <div className="entity-details-top">
                                {/* Quick-info chips: distance, schedule hours, rating */}
                                <div className="entity-details-quick-chips">
                                    {dist && (
                                        <span className="entity-details-chip">
                                            <PinIcon /> {dist} de distância
                                        </span>
                                    )}
                                    {(rating || details.ratingCount > 0) && (
                                        <span className="entity-details-chip entity-details-chip--rating">
                                            <StarIcon /> {rating || '–'}&nbsp;({details.ratingCount})
                                        </span>
                                    )}
                                </div>

                                {/* Address card */}
                                {addressFull && details.showAddress && (
                                    <div className="entity-details-address-card">
                                        <div className="entity-details-address-card-icon">
                                            <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" aria-hidden="true">
                                                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                                            </svg>
                                        </div>
                                        <div className="entity-details-address-card-body">
                                            <span className="entity-details-address-text">{addressFull}</span>
                                            {mapsNavUrl && (
                                                <a
                                                    href={mapsNavUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="entity-details-maps-link"
                                                >
                                                    Abrir no Google Maps →
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Phone card */}
                                {details.phone && (
                                    <div className="entity-details-address-card">
                                        <div className="entity-details-address-card-icon">
                                            <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" aria-hidden="true">
                                                <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V20a1 1 0 01-1 1C10.39 21 3 13.61 3 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.46.57 3.58a1 1 0 01-.25 1.01l-2.2 2.2z"/>
                                            </svg>
                                        </div>
                                        <div className="entity-details-address-card-body">
                                            <span className="entity-details-address-text">{formatPhone(details.phone)}</span>
                                            <button
                                                className="entity-details-maps-link"
                                                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                                                onClick={async () => {
                                                    try {
                                                        await navigator.clipboard.writeText(details.phone.replace(/\D/g, ''));
                                                        toast.success('Telefone copiado!');
                                                    } catch {
                                                        toast.error('Erro ao copiar telefone');
                                                    }
                                                }}
                                            >
                                                Copiar número
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Description */}
                                {details.description && (
                                    <div className="entity-details-description-wrap">
                                        <p className={`entity-details-description${descExpanded ? ' expanded' : ''}`}>
                                            {details.description}
                                        </p>
                                        {details.description.length > 200 && (
                                            <button
                                                className="entity-details-desc-toggle"
                                                onClick={() => setDescExpanded((v) => !v)}
                                            >
                                                {descExpanded ? 'Menos' : 'Continuar lendo'}
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="entity-details-divider" />

                            {/* ── Horário de Funcionamento ── */}
                            {details.openingHours && (
                                <>
                                    <div className="entity-details-section">
                                        <h3 className="entity-details-section-title">Horário de Funcionamento</h3>
                                        <p className="entity-details-hours-text">{details.openingHours}</p>
                                    </div>
                                    <div className="entity-details-divider" />
                                </>
                            )}

                            {/* ── Serviços ── */}
                            {details.services?.length > 0 && (() => {
                                const MAX_SVC = 6;
                                const sorted = [...details.services].sort((a, b) => (a.service?.slug || '').localeCompare(b.service?.slug || ''));
                                const visible = servicesExpanded ? sorted : sorted.slice(0, MAX_SVC);
                                const hidden = sorted.length - MAX_SVC;
                                return (
                                    <>
                                        <div className="entity-details-section">
                                            <h3 className="entity-details-section-title">Serviços</h3>
                                            <div className="entity-details-services-grid">
                                                {visible.map((es) => (
                                                    <div key={es.id} className="entity-details-service-chip">
                                                        <div className="entity-details-service-chip-icon">
                                                            {es.service?.icon ? (
                                                                <img src={es.service.icon} alt="" aria-hidden="true" />
                                                            ) : (
                                                                <div className="entity-details-service-chip-fallback" aria-hidden="true" />
                                                            )}
                                                        </div>
                                                        <span className="entity-details-service-chip-name">{es.service?.name || '–'}</span>
                                                    </div>
                                                ))}
                                                {!servicesExpanded && hidden > 0 && (
                                                    <button
                                                        className="entity-details-service-chip entity-details-service-chip-more"
                                                        onClick={() => setServicesExpanded(true)}
                                                        aria-label={`Ver mais ${hidden} serviços`}
                                                    >
                                                        <div className="entity-details-service-chip-icon entity-details-service-chip-more-icon">
                                                            <span>+{hidden}</span>
                                                        </div>
                                                        <span className="entity-details-service-chip-name">Ver mais</span>
                                                    </button>
                                                )}
                                            </div>
                                            {servicesExpanded && sorted.length > MAX_SVC && (
                                                <button className="entity-details-show-less" onClick={() => setServicesExpanded(false)}>
                                                    Ver menos
                                                </button>
                                            )}
                                        </div>
                                        <div className="entity-details-divider" />
                                    </>
                                );
                            })()}

                            {/* ── Convênios Atendidos ── */}
                            {details.insurances?.length > 0 && (() => {
                                const MAX_INS = 8;
                                const visible = insurancesExpanded ? details.insurances : details.insurances.slice(0, MAX_INS);
                                const hidden = details.insurances.length - MAX_INS;
                                return (
                                    <>
                                        <div className="entity-details-section">
                                            <h3 className="entity-details-section-title">Convênios Atendidos</h3>
                                            <div className="entity-details-insurance-tile-grid">
                                                {visible.map((ins) => (
                                                    <div key={ins.id} className="entity-details-insurance-tile">
                                                        {ins.insurance?.icon && (
                                                            <img
                                                                src={ins.insurance.icon}
                                                                alt={ins.insurance.insurance}
                                                                className="entity-details-insurance-tile-logo"
                                                            />
                                                        )}
                                                        <span className="entity-details-insurance-tile-name">
                                                            {ins.insurance?.insurance || '–'}
                                                        </span>
                                                        {ins.additionalData && (
                                                            <span className="entity-details-insurance-tile-extra">{ins.additionalData}</span>
                                                        )}
                                                    </div>
                                                ))}
                                                {!insurancesExpanded && hidden > 0 && (
                                                    <button
                                                        className="entity-details-insurance-tile entity-details-insurance-tile-more"
                                                        onClick={() => setInsurancesExpanded(true)}
                                                        aria-label={`Ver mais ${hidden} convênios`}
                                                    >
                                                        +{hidden}
                                                    </button>
                                                )}
                                            </div>
                                            {insurancesExpanded && details.insurances.length > MAX_INS && (
                                                <button className="entity-details-show-less" onClick={() => setInsurancesExpanded(false)}>
                                                    Ver menos
                                                </button>
                                            )}
                                        </div>
                                        <div className="entity-details-divider" />
                                    </>
                                );
                            })()}

                            {/* ── Localização ── */}
                            {(addressFull || staticMapUrl) && (
                                <>
                                    <div className="entity-details-section">
                                        <h3 className="entity-details-section-title">Localização</h3>
                                        {details.showAddress ? (
                                            <>
                                                <p className="entity-details-location-text">{addressFull}</p>
                                                <div className="entity-details-location-actions">
                                                    <button className="entity-details-copy-btn" onClick={handleCopyAddress}>
                                                        <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14" aria-hidden="true">
                                                            <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                                                        </svg>
                                                        Copiar
                                                    </button>
                                                    {mapsNavUrl && (
                                                        <a
                                                            href={mapsNavUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="entity-details-copy-btn"
                                                        >
                                                            <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14" aria-hidden="true">
                                                                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                                                            </svg>
                                                            Abrir no Maps
                                                        </a>
                                                    )}
                                                </div>
                                            </>
                                        ) : (
                                            details.serviceRangeActive && (
                                                <p className="entity-details-coverage-text">
                                                    Área de cobertura: {details.serviceRange > 0 ? Math.round(details.serviceRange) : 1} km
                                                </p>
                                            )
                                        )}
                                        {staticMapUrl && (() => {
                                            const mapLatLngUrl = details?.lat && details?.long
                                                ? `https://www.google.com/maps/search/?api=1&query=${details.lat},${details.long}`
                                                : mapsNavUrl;
                                            return (
                                                <a
                                                    href={details.showAddress ? mapLatLngUrl : undefined}
                                                    target={details.showAddress ? '_blank' : undefined}
                                                    rel={details.showAddress ? 'noopener noreferrer' : undefined}
                                                    className={`entity-details-static-map-link${!details.showAddress ? ' entity-details-static-map-link--disabled' : ''}`}
                                                    onClick={!details.showAddress ? (e) => e.preventDefault() : undefined}
                                                >
                                                    <img
                                                        src={staticMapUrl}
                                                        alt="Mapa da localização"
                                                        className="entity-details-static-map"
                                                    />
                                                </a>
                                            );
                                        })()}
                                    </div>
                                    <div className="entity-details-divider" />
                                </>
                            )}

                            {/* ── Avaliações ── */}
                            {details.reviews?.length > 0 && (
                                <>
                                    <div className="entity-details-section">
                                        <div className="entity-details-reviews-header">
                                            <h3 className="entity-details-section-title" style={{ marginBottom: 0 }}>Avaliações</h3>
                                            {(rating || details.ratingCount > 0) && (
                                                <div className="entity-details-reviews-summary">
                                                    <span className="entity-details-reviews-score-big">{rating || '–'}</span>
                                                    <div>
                                                        <div className="entity-details-reviews-stars-row">
                                                            {Array.from({ length: 5 }, (_, i) => (
                                                                <StarIcon key={i} style={{ opacity: i < Math.round(details.ratingScore || 0) ? 1 : 0.2 }} />
                                                            ))}
                                                        </div>
                                                        <p className="entity-details-reviews-count-text">
                                                            {details.ratingCount} {details.ratingCount === 1 ? 'avaliação' : 'avaliações'}
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <div className="entity-details-reviews">
                                            {details.reviews.slice(0, 3).map((rev, i) => (
                                                <div key={i} className="entity-details-review-item">
                                                    <div className="entity-details-review-header">
                                                        <img
                                                            src={rev.user?.image || placeholderSrc}
                                                            alt={rev.user?.name || 'Avatar'}
                                                            className="entity-details-review-avatar-img"
                                                        />
                                                        <div className="entity-details-review-meta">
                                                            <p className="entity-details-review-author">
                                                                {rev.user?.name || rev.userName || 'Anônimo'}
                                                            </p>
                                                            <div className="entity-details-review-stars-date">
                                                                <div className="entity-details-review-stars">
                                                                    {Array.from({ length: 5 }, (_, si) => (
                                                                        <StarIcon key={si} style={{ opacity: si < (rev.score || 5) ? 1 : 0.25 }} />
                                                                    ))}
                                                                </div>
                                                                {rev.createdAt && (
                                                                    <span className="entity-details-review-date">{formatReviewDate(rev.createdAt)}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {(rev.content || rev.comment) && (
                                                        <p className="entity-details-review-comment">{rev.content || rev.comment}</p>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="entity-details-divider" />
                                </>
                            )}

                            {/* ── Disclaimer ── */}
                            <div className="entity-details-disclaimer">
                                Todas as informações e serviços são de responsabilidade do anunciante, a Vetsse não realiza nem participa da prestação de serviços
                            </div>
                        </>
                    ) : null}
                </div>

                {/* ── Footer CTA ── */}
                {!loading && !error && details && (
                    <div className="entity-details-footer">
                        {details.phone ? (
                            <a
                                href={`https://wa.me/${details.phone.replace(/\D/g, '')}?text=Ol%C3%A1%2C+Estou+entrando+em+contato+atrav%C3%A9s+do+seu+perfil+no+app+Vetsse`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="entity-details-cta-conversar"
                                aria-label="Conversar via WhatsApp"
                            >
                                <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" aria-hidden="true">
                                    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
                                </svg>
                                Conversar
                            </a>
                        ) : (
                            <button className="entity-details-cta-conversar" disabled>
                                <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" aria-hidden="true">
                                    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
                                </svg>
                                Conversar
                            </button>
                        )}
                        <button
                            className="entity-details-cta-agendar"
                            onClick={() => toast('No momento, o agendamento está disponível apenas pelo aplicativo Vetsse.', {
                                icon: (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 10, background: '#FEF3C7', flexShrink: 0 }}>
                                        <svg viewBox="0 0 24 24" fill="#D97706" width="22" height="22" aria-hidden="true">
                                            <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                                        </svg>
                                    </div>
                                ),
                            })}
                            style={{ display: canSchedule ? undefined : 'none' }}
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" aria-hidden="true">
                                <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
                            </svg>
                            Agendar
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── Location Modal ───────────────────────────────────────────────────────────

function LocationModal({ onClose, onSelect }) {
    const containerRef = useRef(null);

    useEffect(() => {
        let element = null;
        let listener = null;

        loadGoogleMaps().then((gmaps) => {
            if (!containerRef.current) return;

            if (gmaps.places?.PlaceAutocompleteElement) {
                // New API (recommended as of March 2025)
                element = new gmaps.places.PlaceAutocompleteElement({
                    types: ['geocode'],
                });
                containerRef.current.appendChild(element);

                listener = async (event) => {
                    const place = event.place;
                    await place.fetchFields({ fields: ['location', 'formattedAddress', 'displayName'] });
                    if (!place.location) return;
                    const lat = place.location.lat();
                    const lng = place.location.lng();
                    const name = place.formattedAddress || place.displayName || 'Local selecionado';
                    onSelect(lat, lng, name);
                };
                element.addEventListener('gmp-placeselect', listener);
            } else if (gmaps.places?.Autocomplete) {
                // Legacy fallback
                const input = document.createElement('input');
                input.placeholder = 'Digite uma cidade ou endereço...';
                input.className = 'location-modal-input';
                input.autoFocus = true;
                containerRef.current.appendChild(input);

                const ac = new gmaps.places.Autocomplete(input, { types: ['geocode'] });
                listener = () => {
                    const place = ac.getPlace();
                    if (!place.geometry?.location) return;
                    onSelect(
                        place.geometry.location.lat(),
                        place.geometry.location.lng(),
                        place.formatted_address || place.name || 'Local selecionado'
                    );
                };
                ac.addListener('place_changed', listener);
                element = { removeEventListener: () => gmaps.event.clearInstanceListeners(ac) };
            }
        }).catch(() => {});

        return () => {
            if (element && listener) {
                try { element.removeEventListener('gmp-placeselect', listener); } catch {}
            }
            if (containerRef.current) containerRef.current.innerHTML = '';
        };
    }, [onSelect]);

    function handleUseGPS() {
        if (!navigator.geolocation) return;
        navigator.geolocation.getCurrentPosition(
            (pos) => onSelect(pos.coords.latitude, pos.coords.longitude, null),
            () => {},
            { timeout: 8000, enableHighAccuracy: true }
        );
    }

    return (
        <div className="location-modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
            <div className="location-modal" onClick={(e) => e.stopPropagation()}>
                <div className="location-modal-header">
                    <h2 className="location-modal-title">Alterar localização</h2>
                    <button className="location-modal-close" onClick={onClose} aria-label="Fechar">
                        <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                        </svg>
                    </button>
                </div>
                <div className="location-modal-body">
                    <div ref={containerRef} className="location-modal-pac-container" />
                    <button className="location-gps-btn" onClick={handleUseGPS}>
                        <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" aria-hidden="true">
                            <path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3c-.46-4.17-3.77-7.48-7.94-7.94V1h-2v2.06C6.83 3.52 3.52 6.83 3.06 11H1v2h2.06c.46 4.17 3.77 7.48 7.94 7.94V23h2v-2.06c4.17-.46 7.48-3.77 7.94-7.94H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z" />
                        </svg>
                        Usar minha localização atual
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function Discover() {
    const { entityId: entityIdParam } = useParams();
    const navigate = useNavigate();
    const [viewMode, setViewMode] = useState('list');
    const [loading, setLoading] = useState(true);
    const [entities, setEntities] = useState([]);
    const [baseEntities, setBaseEntities] = useState([]);
    const [services, setServices] = useState([]);
    const [selectedService, setSelectedService] = useState(null);
    const [selectedEntity, setSelectedEntity] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [userLocation, setUserLocation] = useState(null);
    const [distance, setDistance] = useState(DEFAULT_DISTANCE);
    const [locationName, setLocationName] = useState('');
    const [initError, setInitError] = useState(null);
    const [mapError, setMapError] = useState(null);
    const [locationLoading, setLocationLoading] = useState(false);
    const [locationModalOpen, setLocationModalOpen] = useState(false);
    const [detailsEntityId, setDetailsEntityId] = useState(
        entityIdParam || null
    );
    const [detailsEntityName, setDetailsEntityName] = useState(null);

    // Refs for stale-closure safety in async/event handlers
    const appTokenRef = useRef(null);
    const selectedServiceRef = useRef(null);
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const userLocationRef = useRef(null);
    const distanceRef = useRef(DEFAULT_DISTANCE);
    const circlesRef = useRef([]);
    const markersRef = useRef([]);
    const searchTimerRef = useRef(null);

    // Keep refs in sync
    useEffect(() => { selectedServiceRef.current = selectedService; }, [selectedService]);
    useEffect(() => { userLocationRef.current = userLocation; }, [userLocation]);
    useEffect(() => { distanceRef.current = distance; }, [distance]);

    // ── SEO ─────────────────────────────────────────────────────────────────────

    useEffect(() => {
        const isEntityPage = !!detailsEntityName;
        const pageTitle = isEntityPage
            ? `${detailsEntityName} | Vetsse`
            : 'Encontre Veterinários Perto de Você | Vetsse';
        const pageDescription = isEntityPage
            ? `Veja o perfil de ${detailsEntityName} no Vetsse. Serviços, avaliações, localização e muito mais.`
            : 'Descubra veterinários e clínicas veterinárias próximos a você. Compare serviços, avaliações e agende uma consulta. Grátis no Vetsse.';
        const pageUrl = isEntityPage
            ? `${window.location.origin}/vets/${detailsEntityId}`
            : `${window.location.origin}/vets`;

        document.title = pageTitle;

        const metas = [
            { name: 'description', content: pageDescription },
            { name: 'keywords', content: 'veterinário perto de mim, clínica veterinária, consulta veterinária, veterinário online, Vetsse, petcare' },
            { name: 'robots', content: 'index, follow' },
            { property: 'og:title', content: pageTitle },
            { property: 'og:description', content: pageDescription },
            { property: 'og:type', content: isEntityPage ? 'business.business' : 'website' },
            { property: 'og:url', content: pageUrl },
            { property: 'og:site_name', content: 'Vetsse' },
            { property: 'og:locale', content: 'pt_BR' },
            { name: 'twitter:card', content: 'summary_large_image' },
            { name: 'twitter:title', content: pageTitle },
            { name: 'twitter:description', content: isEntityPage
                ? `Veja o perfil de ${detailsEntityName} no Vetsse.`
                : 'Descubra veterinários e clínicas veterinárias próximos a você. Grátis no Vetsse.' },
        ];

        metas.forEach(({ name, property, content }) => {
            const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
            let tag = document.querySelector(selector);
            if (!tag) {
                tag = document.createElement('meta');
                if (name) tag.name = name;
                if (property) tag.setAttribute('property', property);
                document.head.appendChild(tag);
            }
            tag.content = content;
        });

        // Canonical link
        let canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) {
            canonical = document.createElement('link');
            canonical.rel = 'canonical';
            document.head.appendChild(canonical);
        }
        canonical.href = pageUrl;

        // JSON-LD structured data
        let jsonLd = document.getElementById('vetsse-discover-jsonld');
        if (!jsonLd) {
            jsonLd = document.createElement('script');
            jsonLd.type = 'application/ld+json';
            jsonLd.id = 'vetsse-discover-jsonld';
            document.head.appendChild(jsonLd);
        }
        const schema = isEntityPage
            ? {
                '@context': 'https://schema.org',
                '@type': 'VeterinaryCare',
                name: detailsEntityName,
                url: pageUrl,
            }
            : {
                '@context': 'https://schema.org',
                '@type': 'WebPage',
                name: 'Encontre Veterinários Perto de Você',
                description: 'Descubra veterinários e clínicas veterinárias próximos a você. Compare serviços, avaliações e agende uma consulta.',
                url: pageUrl,
                inLanguage: 'pt-BR',
                publisher: {
                    '@type': 'Organization',
                    name: 'Vetsse',
                    url: window.location.origin,
                },
            };
        jsonLd.textContent = JSON.stringify(schema);

        return () => {
            document.title = 'Encontre Veterinários Perto de Você | Vetsse';
        };
    }, [detailsEntityName, detailsEntityId]);

    // ── Init ─────────────────────────────────────────────────────────────────────

    const doFetchEntities = useCallback(async (token, lat, lng, dist, svcId = -1) => {
        setLoading(true);
        try {
            const userLoc = userLocationRef.current;
            const defaultLat = userLoc?.lat ?? lat;
            const defaultLng = userLoc?.lng ?? lng;
            const res = await publicApi.get('/v1/api/entities', {
                headers: { Authorization: token },
                params: {
                    lat: String(lat),
                    long: String(lng),
                    distanceLimit: String(Math.max(dist, DEFAULT_DISTANCE)),
                    serviceId: String(svcId),
                    defaultLat: String(defaultLat),
                    defaultLong: String(defaultLng),
                },
            });
            const sorted = sortEntities(res.data?.response || []);
            setBaseEntities(sorted);

            const svc = selectedServiceRef.current;
            if (svc) {
                setEntities(sorted.filter((e) =>
                    e.services?.some((es) => es.service?.id === svc.id)
                ));
            } else {
                setEntities(sorted);
            }
        } catch (err) {
            console.error('[Discover] fetchEntities error:', err);
            setInitError('Não foi possível carregar os veterinários. Tente recarregar a página.');
        } finally {
            setLoading(false);
        }
    }, []);

    const doFetchServices = useCallback(async (token) => {
        try {
            const res = await publicApi.get('/v1/api/services', {
                headers: { Authorization: token },
            });
            const sorted = (res.data?.response || []).sort((a, b) =>
                (a.slug || '').localeCompare(b.slug || '')
            );
            setServices(sorted);
        } catch (err) {
            console.error('[Discover] fetchServices error:', err);
        }
    }, []);

    const reverseGeocode = useCallback(async (lat, lng, persist = false) => {
        const BR_STATES = {
            'acre': 'AC', 'alagoas': 'AL', 'amapá': 'AP', 'amazonas': 'AM',
            'bahia': 'BA', 'ceará': 'CE', 'distrito federal': 'DF',
            'espírito santo': 'ES', 'goiás': 'GO', 'maranhão': 'MA',
            'mato grosso do sul': 'MS', 'mato grosso': 'MT', 'minas gerais': 'MG',
            'pará': 'PA', 'paraíba': 'PB', 'paraná': 'PR', 'pernambuco': 'PE',
            'piauí': 'PI', 'rio de janeiro': 'RJ', 'rio grande do norte': 'RN',
            'rio grande do sul': 'RS', 'rondônia': 'RO', 'roraima': 'RR',
            'santa catarina': 'SC', 'são paulo': 'SP', 'sergipe': 'SE',
            'tocantins': 'TO',
        };
        try {
            const res = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=12&addressdetails=1`,
                { headers: { 'Accept-Language': 'pt-BR' } }
            );
            const data = await res.json();
            const city =
                data.address?.city ||
                data.address?.town ||
                data.address?.municipality ||
                data.address?.county ||
                '';
            const state = data.address?.state || '';
            const stateCode = data.address?.state_code?.toUpperCase()
                || data.address?.['ISO3166-2-lvl4']?.split('-')[1]
                || BR_STATES[state.toLowerCase()]
                || '';
            const displayName = city ? `${city}${stateCode ? `, ${stateCode}` : ''}` : 'Localização atual';
            setLocationName(displayName);
            if (persist) persistLocation(lat, lng, displayName);
        } catch {
            setLocationName('Localização atual');
        }
    }, []);

    useEffect(() => {
        let canceled = false;

        async function init() {
            try {
                const token = await resolveToken();
                if (canceled) return;
                appTokenRef.current = token;

                await doFetchServices(token);
                if (canceled) return;

                const saved = getSavedLocation();
                if (saved) {
                    if (!canceled) {
                        setUserLocation({ lat: saved.lat, lng: saved.lng });
                        setLocationName(saved.name || 'Localização salva');
                        reverseGeocode(saved.lat, saved.lng, true);
                        await doFetchEntities(token, saved.lat, saved.lng, DEFAULT_DISTANCE);
                    }
                } else {
                    navigator.geolocation.getCurrentPosition(
                    async (pos) => {
                        if (canceled) return;
                        const lat = pos.coords.latitude;
                        const lng = pos.coords.longitude;
                        setUserLocation({ lat, lng });
                        reverseGeocode(lat, lng, true);
                        await doFetchEntities(token, lat, lng, DEFAULT_DISTANCE);
                    },
                    async () => {
                        if (canceled) return;
                        setUserLocation({ lat: FALLBACK_LAT, lng: FALLBACK_LNG });
                        setLocationName('Brasil');
                        setLocationModalOpen(true);
                        await doFetchEntities(token, FALLBACK_LAT, FALLBACK_LNG, DEFAULT_DISTANCE);
                    },
                    { timeout: 8000, enableHighAccuracy: true }
                    );
                }
            } catch (err) {
                if (!canceled) {
                    console.error('[Discover] init error:', err);
                    setInitError('Erro ao inicializar. Verifique sua conexão e tente recarregar.');
                    setLoading(false);
                }
            }
        }

        init();
        return () => { canceled = true; };
    }, [doFetchEntities, doFetchServices, reverseGeocode]);

    // ── Service filter (local) ───────────────────────────────────────────────────

    function handleSelectService(svc) {
        setSelectedService(svc);
        if (!svc) {
            setEntities(baseEntities);
        } else {
            setEntities(
                baseEntities.filter((e) =>
                    e.services?.some((es) => es.service?.id === svc.id)
                )
            );
        }
    }

    // ── Search (debounced) ───────────────────────────────────────────────────────

    function handleSearchChange(value) {
        setSearchText(value);
        clearTimeout(searchTimerRef.current);

        if (!value.trim()) {
            const svc = selectedServiceRef.current;
            if (svc) {
                setEntities(baseEntities.filter((e) =>
                    e.services?.some((es) => es.service?.id === svc.id)
                ));
            } else {
                setEntities(baseEntities);
            }
            return;
        }

        searchTimerRef.current = setTimeout(async () => {
            const token = appTokenRef.current;
            const loc = userLocationRef.current;
            if (!token || !loc) return;
            try {
                const res = await publicApi.get('/v1/api/entities/search', {
                    headers: { Authorization: token },
                    params: {
                        lat: String(loc.lat),
                        long: String(loc.lng),
                        distanceLimit: String(Math.max(distanceRef.current, DEFAULT_DISTANCE)),
                        serviceId: String(selectedServiceRef.current?.id ?? -1),
                        search: value,
                        defaultLat: String(loc.lat),
                        defaultLong: String(loc.lng),
                    },
                });
                setEntities(sortEntities(res.data?.response || []));
            } catch (err) {
                console.error('[Discover] search error:', err);
            }
        }, 450);
    }

    // ── Map ──────────────────────────────────────────────────────────────────────

    const clearMapMarkers = useCallback(() => {
        markersRef.current.forEach((m) => m.setMap(null));
        markersRef.current = [];
        circlesRef.current.forEach((c) => c.setMap(null));
        circlesRef.current = [];
    }, []);

    const addMapMarkers = useCallback((map, entList) => {
        clearMapMarkers();
        const gmaps = window.google?.maps;
        if (!gmaps) return;

        entList.forEach((entity) => {
            const isPersonal = entity.type === 'personal';
            const lat = isPersonal
                ? Math.round(entity.lat * 100) / 100
                : entity.lat;
            const lng = isPersonal
                ? Math.round(entity.long * 100) / 100
                : entity.long;

            if (!lat || !lng) return;

            const markerUrl =
                entity.type === 'personal' ? markerVet
                : entity.type === 'others'  ? markerOther
                : markerClinic;

            const icon = {
                url: markerUrl,
                scaledSize: new gmaps.Size(60, 60),
                anchor: new gmaps.Point(20, 20),
            };

            const marker = new gmaps.Marker({
                position: { lat, lng },
                map,
                icon,
                title: entity.name,
            });

            marker.addListener('click', () => {
                setSelectedEntity(entity);
                map.panTo({ lat, lng });
                const smoothZoomTo = (target) => {
                    const cur = map.getZoom();
                    if (cur === target) return;
                    const step = cur < target ? 1 : -1;
                    gmaps.event.addListenerOnce(map, 'zoom_changed', () => smoothZoomTo(target));
                    setTimeout(() => map.setZoom(cur + step), 100);
                };
                smoothZoomTo(16);

                // Draw service range circle for personal
                circlesRef.current.forEach((c) => c.setMap(null));
                circlesRef.current = [];
                if (isPersonal && entity.serviceRangeActive && entity.serviceRange > 0) {
                    const circle = new gmaps.Circle({
                        center: { lat, lng },
                        radius: entity.serviceRange * 1000,
                        map,
                        fillColor: '#7165FF',
                        fillOpacity: 0.08,
                        strokeColor: '#7165FF',
                        strokeWeight: 2,
                        strokeOpacity: 0.6,
                    });
                    circlesRef.current.push(circle);
                }
            });

            markersRef.current.push(marker);
        });
    }, [clearMapMarkers]);

    const initMap = useCallback(async (lat, lng, entList) => {
        if (mapInstanceRef.current) return;
        if (!mapRef.current) return;
        setMapError(null);
        try {
            const gmaps = await loadGoogleMaps();
            if (!mapRef.current || mapInstanceRef.current) return;

            const map = new gmaps.Map(mapRef.current, {
                center: { lat, lng },
                zoom: 12,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false,
                styles: [
                    { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
                    { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
                    { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
                    { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
                    { featureType: 'administrative.land_parcel', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
                    { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
                    { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
                    { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
                    { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
                    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
                    { featureType: 'road.arterial', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
                    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }] },
                    { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
                    { featureType: 'road.local', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
                    { featureType: 'transit.line', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
                    { featureType: 'transit.station', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
                    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9c9c9' }] },
                    { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
                ],
            });

            // User location dot
            const userSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" fill="#7165FF" stroke="white" stroke-width="3"/></svg>`;
            new gmaps.Marker({
                position: { lat, lng },
                map,
                icon: {
                    url: `data:image/svg+xml,${encodeURIComponent(userSvg)}`,
                    scaledSize: new gmaps.Size(24, 24),
                    anchor: new gmaps.Point(12, 12),
                },
                title: 'Você está aqui',
                zIndex: 999,
            });

            // Click on map to deselect entity
            map.addListener('click', () => setSelectedEntity(null));

            // After map finishes moving, recalculate radius and re-fetch
            map.addListener('idle', () => {
                const center = map.getCenter();
                const bounds = map.getBounds();
                if (!center || !bounds) return;

                const ne = bounds.getNorthEast();
                // 1 degree latitude ≈ 111 km
                const radiusKm = Math.max(Math.abs(ne.lat() - center.lat()) * 111, DEFAULT_DISTANCE);
                setDistance(radiusKm);

                const token = appTokenRef.current;
                const svc = selectedServiceRef.current;
                if (token) {
                    doFetchEntities(token, center.lat(), center.lng(), radiusKm, svc?.id ?? -1);
                }
            });

            mapInstanceRef.current = map;
            addMapMarkers(map, entList);
        } catch (err) {
            console.error('[Discover] map error:', err);
            setMapError('Não foi possível carregar o mapa. Verifique sua conexão.');
        }
    }, [doFetchEntities, addMapMarkers]);

    // Switch to map mode
    useEffect(() => {
        if (viewMode === 'map' && userLocation && !mapInstanceRef.current) {
            const timer = setTimeout(() => {
                initMap(userLocation.lat, userLocation.lng, entities);
            }, 80);
            return () => clearTimeout(timer);
        }
    }, [viewMode, userLocation, initMap, entities]);

    // Update markers when entities change in map mode
    useEffect(() => {
        if (viewMode === 'map' && mapInstanceRef.current && window.google?.maps) {
            addMapMarkers(mapInstanceRef.current, entities);
        }
    }, [entities, viewMode, addMapMarkers]);

    // Cleanup map on unmount
    useEffect(() => {
        return () => {
            clearTimeout(searchTimerRef.current);
            if (mapInstanceRef.current && window.google?.maps) {
                window.google.maps.event.clearInstanceListeners(mapInstanceRef.current);
            }
            clearMapMarkers();
            mapInstanceRef.current = null;
        };
    }, [clearMapMarkers]);

    // ── Location refresh ─────────────────────────────────────────────────────────

    const handleSelectLocation = useCallback(async (lat, lng, name) => {
        setLocationModalOpen(false);
        setLocationLoading(true);
        setUserLocation({ lat, lng });
        if (name) {
            setLocationName(name);
            persistLocation(lat, lng, name);
        } else {
            reverseGeocode(lat, lng, true);
        }
        if (mapInstanceRef.current) {
            mapInstanceRef.current.setCenter({ lat, lng });
            mapInstanceRef.current.setZoom(12);
        }
        const token = appTokenRef.current;
        if (token) {
            await doFetchEntities(token, lat, lng, DEFAULT_DISTANCE, selectedServiceRef.current?.id ?? -1);
        }
        setLocationLoading(false);
    }, [doFetchEntities, reverseGeocode]);

    // ── View mode toggle ──────────────────────────────────────────────────────────

    function handleToggleMode() {
        const next = viewMode === 'list' ? 'map' : 'list';
        setViewMode(next);
        setSelectedEntity(null);
        // Keep the map instance alive so center/zoom persist when switching back
    }

    function handleRetry() {
        setInitError(null);
        setLoading(true);
        appTokenRef.current = null;
        resolveToken()
            .then(async (token) => {
                appTokenRef.current = token;
                await doFetchServices(token);
                const loc = userLocationRef.current || { lat: FALLBACK_LAT, lng: FALLBACK_LNG };
                await doFetchEntities(token, loc.lat, loc.lng, DEFAULT_DISTANCE);
            })
            .catch(() => {
                setInitError('Erro ao reconectar. Tente recarregar a página.');
                setLoading(false);
            });
    }

    // ── Render ────────────────────────────────────────────────────────────────────

    const structuredData = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Descobrir Veterinários — Vetsse',
        description: 'Encontre veterinários e clínicas veterinárias próximos a você.',
        url: `${window.location.origin}/vets`,
        publisher: {
            '@type': 'Organization',
            name: 'Vetsse',
            url: window.location.origin,
        },
    };

    return (
        <div className="discover-page">
            {/* Structured data for SEO */}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
            />

            {/* ── Header ── */}
            <header className="discover-header" role="banner">
                <div className="discover-header-inner">
                    <Logo className="dashboard-logo" />
                    <Link to="/" className="discover-logo-link" aria-label="Vetsse — Início">
                        <img src={logoSrc} alt="Vetsse" className="discover-logo" />
                    </Link>
                    <nav className="discover-header-nav" aria-label="Navegação principal">
                        <Link to="/dashboard" className="discover-btn-login" target="_blank">
                            Sou veterinário
                        </Link>
                    </nav>
                </div>
            </header>

            {/* ── Sticky search + filters ── */}
            <div className="discover-sticky" role="search">
                <div className="discover-search-section">
                    <div className="discover-search-bar">
                        <div className="search-input-wrapper">
                            <svg
                                className="search-icon"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                aria-hidden="true"
                            >
                                <circle cx="11" cy="11" r="8" />
                                <path d="m21 21-4.35-4.35" />
                            </svg>
                            <input
                                type="search"
                                className="search-input"
                                placeholder="O que você procura?"
                                value={searchText}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                aria-label="Buscar veterinários ou clínicas"
                            />
                            {searchText && (
                                <button
                                    className="search-clear"
                                    onClick={() => handleSearchChange('')}
                                    aria-label="Limpar busca"
                                >
                                    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                                        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                                    </svg>
                                </button>
                            )}
                        </div>

                        <button
                            className={`discover-location-pill${locationLoading ? ' loading' : ''}`}
                            onClick={() => setLocationModalOpen(true)}
                            aria-label="Alterar localização"
                            title="Clique para alterar a localização"
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14" aria-hidden="true">
                                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                            </svg>
                            <span className="location-name">
                                {locationLoading ? 'Buscando...' : locationName || 'Minha localização'}
                            </span>
                            <span className="distance-badge">
                                {Math.ceil(Math.max(distance, DEFAULT_DISTANCE))} km
                            </span>
                        </button>
                    </div>
                </div>

                {/* Service tabs */}
                {services.length > 0 && (
                    <div className="discover-service-tabs" role="tablist" aria-label="Filtrar por serviço">
                        <button
                            role="tab"
                            aria-selected={!selectedService}
                            className={`service-tab${!selectedService ? ' active' : ''}`}
                            onClick={() => handleSelectService(null)}
                        >
                            Todos
                        </button>
                        {services.map((svc) => (
                            <button
                                key={svc.id}
                                role="tab"
                                aria-selected={selectedService?.id === svc.id}
                                className={`service-tab${selectedService?.id === svc.id ? ' active' : ''}`}
                                onClick={() =>
                                    handleSelectService(selectedService?.id === svc.id ? null : svc)
                                }
                            >
                                {svc.icon && (
                                    <img
                                        src={svc.icon}
                                        alt=""
                                        className="service-tab-icon"
                                        aria-hidden="true"
                                        loading="lazy"
                                    />
                                )}
                                {svc.name}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* ── Main content ── */}
            <main className="discover-main" id="main-content" aria-live="polite">
                {/* List view */}
                <div
                    className="discover-list-view"
                    style={{ display: viewMode === 'list' ? 'block' : 'none' }}
                >
                    {initError ? (
                        <div className="discover-error" role="alert">
                            <span>⚠️</span>
                            <p>{initError}</p>
                            <button className="discover-btn-primary" onClick={handleRetry}>
                                Tentar novamente
                            </button>
                        </div>
                    ) : loading ? (
                        <div className="discover-cards-grid">
                            {Array.from({ length: 8 }, (_, i) => (
                                <SkeletonCard key={i} />
                            ))}
                        </div>
                    ) : entities.length === 0 ? (
                        <EmptyState
                            onRefresh={() => {
                                const token = appTokenRef.current;
                                const loc = userLocationRef.current;
                                if (token && loc) doFetchEntities(token, loc.lat, loc.lng, DEFAULT_DISTANCE);
                            }}
                        />
                    ) : (
                        <>
                            <p className="discover-results-count" aria-live="polite">
                                <strong>{entities.length}</strong>{' '}
                                {entities.length === 1 ? 'resultado' : 'resultados'}
                                {selectedService ? (
                                    <> para <strong>"{selectedService.name}"</strong></>
                                ) : null}
                            </p>
                            <div className="discover-cards-grid">
                                {entities.map((entity) => (
                                    <EntityCard
                                        key={entity.id}
                                        entity={entity}
                                        onClick={() => { setDetailsEntityId(entity.subId); setDetailsEntityName(entity.name); navigate(`/vets/${entity.subId}`, { replace: true }); }}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </div>

                {/* Map view */}
                <div
                    className="discover-map-view"
                    style={{ display: viewMode === 'map' ? 'flex' : 'none' }}
                    aria-label="Mapa de veterinários"
                >
                    {!userLocation && (
                        <div className="discover-map-loading" role="status" aria-label="Carregando mapa">
                            <div className="map-spinner" aria-hidden="true" />
                            <p>Obtendo sua localização...</p>
                        </div>
                    )}
                    {mapError && (
                        <div className="discover-map-error" role="alert">
                            <span>⚠️</span>
                            <p>{mapError}</p>
                            <button
                                className="discover-btn-primary"
                                onClick={() => {
                                    setMapError(null);
                                    mapInstanceRef.current = null;
                                    const loc = userLocationRef.current;
                                    if (loc) initMap(loc.lat, loc.lng, entities);
                                }}
                            >
                                Recarregar mapa
                            </button>
                        </div>
                    )}
                    <div ref={mapRef} className="discover-map-container" />

                    {selectedEntity && (
                        <MapEntityCard
                            entity={selectedEntity}
                            onOpen={(e) => { setDetailsEntityId(e.subId); setDetailsEntityName(e.name); navigate(`/vets/${e.subId}`, { replace: true }); }}
                        />
                    )}
                </div>
            </main>

            {locationModalOpen && (
                <LocationModal
                    onClose={() => setLocationModalOpen(false)}
                    onSelect={handleSelectLocation}
                />
            )}

            {detailsEntityId && (
                <EntityDetailsModal
                    entityId={detailsEntityId}
                    token={appTokenRef.current}
                    defaultLat={userLocation?.lat ?? FALLBACK_LAT}
                    defaultLong={userLocation?.lng ?? FALLBACK_LNG}
                    onNameLoad={(name) => setDetailsEntityName(name)}
                    onClose={() => { setDetailsEntityId(null); setDetailsEntityName(null); navigate('/vets', { replace: true }); }}
                />
            )}

            {/* ── FAB: toggle mode ── */}
            <button
                className="discover-mode-fab"
                onClick={handleToggleMode}
                aria-label={viewMode === 'list' ? 'Ver no mapa' : 'Ver em lista'}
            >
                {viewMode === 'list' ? (
                    <>
                        <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" aria-hidden="true">
                            <path d="M20.5 3l-.16.03L15 5.1 9 3 3.36 4.9c-.21.07-.36.25-.36.48V20.5c0 .28.22.5.5.5l.16-.03L9 18.9l6 2.1 5.64-1.9c.21-.07.36-.25.36-.48V3.5c0-.28-.22-.5-.5-.5zM15 19l-6-2.11V5l6 2.11V19z" />
                        </svg>
                        Mapa
                    </>
                ) : (
                    <>
                        <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" aria-hidden="true">
                            <path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z" />
                        </svg>
                        Lista
                    </>
                )}
            </button>
        </div>
    );
}
